import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createCheckout, billingStatus, redeemPromo, fetchPublicSettings, fetchPublicPlans } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";
import { Check, Loader2, ArrowLeft, Zap, Ticket, Sparkles, Gift } from "lucide-react";
import { toast } from "sonner";

const PRO_FEATURES = [
  "Unlimited reports (fair use)", "Unlimited revised drafts & improvement tracking",
  "Side-by-side draft comparison", "Full examiner reports", "Grade prediction + confidence",
  "Priority improvements", "\u201CWhat do I need for a First?\u201D", "Assignment history & version timeline",
  "Academic Profile dashboard", "PDF reports", "Priority processing",
];
const PROMO_FREE_FEATURES = [
  "Unlimited reports during launch (fair-use limits apply)",
  "Predicted grade + confidence",
  "Submission readiness",
  "Executive summary + examiner-style feedback",
  "Referencing & structure checks",
  "PDF download",
  "Guest mode or free account",
];
const FREE_FEATURES = ["2 full examiner reports", "Predicted grade", "Submission readiness", "Executive summary", "Basic improvement suggestions", "Initial analysis only — no revised drafts", "PDF download", "Guest mode or free account"];

// £-formatted price derived from the authoritative API payload (`amount_gbp`
// is a 2dp string, always).
const fmt = (p) => `£${p?.amount_gbp ?? "—"}`;

// While `settings.free_until_further_notice` is on, every price is displayed
// with a strikethrough + "FREE" replacement so the future price stays visible
// but users see they pay nothing today.
const FreePrice = ({ children, className = "" }) => (
  <span className={`inline-flex items-baseline gap-2 ${className}`}>
    <span className="line-through decoration-2 opacity-60">{children}</span>
    <span className="font-mono font-semibold text-emerald-600 text-2xl">FREE</span>
  </span>
);

export default function Pricing() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [loadingId, setLoadingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [settings, setSettings] = useState({ free_until_further_notice: false });
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    track("upgrade_viewed");
    billingStatus().then(setStatus).catch(() => {});
    fetchPublicSettings().then(setSettings).catch(() => {});
    fetchPublicPlans().then((d) => setPlans(d?.plans || [])).catch(() => {});
  }, []);

  // Derived plan lookups — single source of truth is the API payload.
  const proMonthly = useMemo(() => plans.find((p) => p.slug === "pro_monthly"), [plans]);
  const proYearly  = useMemo(() => plans.find((p) => p.slug === "pro_yearly"),  [plans]);
  const creditPacks = useMemo(() => {
    const CRED_TAGS = { credits_3: "Save 15%", credits_10: "Best value" };
    return plans
      .filter((p) => p.type === "credits")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((p) => ({ id: p.slug, n: p.label || `${p.credits} reports`, price: fmt(p), tag: CRED_TAGS[p.slug] }));
  }, [plans]);

  const promoOn = !!settings?.free_until_further_notice;

  const buy = async (packageId) => {
    if (promoOn) {
      toast.info("Everything is FREE right now — no need to check out. Just sign up.");
      if (!user) setAuthOpen({ open: true, mode: "register" });
      else navigate("/new");
      return;
    }
    if (!user) { toast.message("Please sign in first to upgrade."); setAuthOpen(true); return; }
    setLoadingId(packageId);
    try {
      track("checkout_started", { package: packageId });
      const { url } = await createCheckout(packageId);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoadingId(null);
    }
  };

  const redeem = async () => {
    const c = redeemCode.trim().toUpperCase();
    if (!c) { toast.error("Enter a promo code."); return; }
    if (!user) { toast.message("Please sign in first to redeem a code."); setAuthOpen(true); return; }
    setRedeeming(true);
    try {
      const res = await redeemPromo(c);
      track("promo_redeemed", { code: c, benefit_type: res.benefit_type });
      toast.success(res.message || "Promo code redeemed.");
      setRedeemCode("");
      // Refetch billing status so the header + Pro badge update immediately
      const fresh = await billingStatus();
      setStatus(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem this code.");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <div className="border-b border-border bg-card px-6 md:px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-oxford hover:underline" data-testid="pricing-back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        {status && <span className="text-sm text-muted-foreground">{status.plan === "pro" ? "You're on Pro" : status.promo_on ? `Unlimited reports — ${status.fair_use_remaining ?? "∞"} left in this ${status.fair_use_window_hours}h window` : `${status.remaining_free} free report${status.remaining_free === 1 ? "" : "s"} left${status.credits ? ` · ${status.credits} credits` : ""}`}</span>}
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        {promoOn && (
          <div className="mb-8 relative overflow-hidden border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 p-5 sm:p-6" data-testid="promo-banner">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0"><Gift size={20} /></div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-600 text-white px-2 py-1" data-testid="promo-banner-badge">{settings.promo_banner_text || "FREE — Free until further notice"}</span>
                  <span className="text-xs text-emerald-800 font-medium">MyAE Public Launch</span>
                </div>
                <p className="mt-2 text-sm text-zinc-700 leading-relaxed" data-testid="promo-banner-subline">
                  {settings.promo_subline || "Every plan is currently free while we grow. Prices shown are our future prices."}
                </p>
              </div>
            </div>
          </div>
        )}

        <p className="overline text-oxford mb-2 text-center">Pricing</p>
        <h1 className="font-serif text-5xl tracking-tight text-center mb-2">Know your grade before you submit</h1>
        <p className="text-muted-foreground text-center mb-10">{promoOn ? "Every feature is free right now. See our future prices below." : "Start free. Upgrade when you need more."}</p>

        <div className="grid md:grid-cols-2 gap-px bg-border border border-border mb-px">
          {/* Free */}
          <div className="bg-card p-8" data-testid="plan-free">
            <p className="overline text-muted-foreground">Free</p>
            <p className="font-mono text-4xl font-semibold mt-2">£0</p>
            <p className="text-sm text-muted-foreground mt-1">Perfect for trying the platform.</p>
            <ul className="mt-6 space-y-2">
              {(promoOn ? PROMO_FREE_FEATURES : FREE_FEATURES).map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check size={16} className="text-muted-foreground mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <button onClick={() => navigate("/")} className="mt-8 w-full border border-border py-3 text-sm hover:bg-secondary transition-colors" data-testid="plan-free-btn">Continue free</button>
          </div>

          {/* Pro */}
          <div className="bg-oxford text-white p-8 relative" data-testid="plan-pro">
            <span className="absolute top-4 right-4 bg-white/15 text-[10px] uppercase tracking-wider px-2 py-1 flex items-center gap-1"><Zap size={11} /> Most popular</span>
            <p className="overline text-white/60">Student Pro</p>
            {promoOn ? (
              <div className="mt-2" data-testid="pro-price-promo">
                <FreePrice className="font-mono text-4xl font-semibold text-white/85">{proMonthly ? `${fmt(proMonthly)}/mo` : "—"}</FreePrice>
                <p className="text-xs text-white/70 mt-2">Future price: {proMonthly ? `${fmt(proMonthly)}/mo` : "—"}{proYearly ? ` · ${fmt(proYearly)}/yr` : ""} — <span className="font-semibold">free until further notice</span></p>
              </div>
            ) : (
              <>
                <p className="font-mono text-4xl font-semibold mt-2">{proMonthly ? fmt(proMonthly) : "—"}<span className="text-lg text-white/70">/mo</span></p>
                {proYearly && <p className="text-sm text-white/70 mt-1">or {fmt(proYearly)}/year</p>}
              </>
            )}
            <ul className="mt-6 space-y-2">
              {PRO_FEATURES.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check size={16} className="text-white mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <div className="mt-8 grid grid-cols-2 gap-3">
              <button onClick={() => buy("pro_monthly")} disabled={loadingId} data-testid="buy-pro-monthly"
                className="bg-white text-oxford py-3 text-sm font-medium hover:bg-white/90 transition-colors flex items-center justify-center gap-2">
                {loadingId === "pro_monthly" ? <Loader2 size={15} className="animate-spin" /> : null} {promoOn ? "Get Pro Free" : "Monthly"}
              </button>
              <button onClick={() => buy("pro_yearly")} disabled={loadingId} data-testid="buy-pro-yearly"
                className="bg-white/10 border border-white/30 text-white py-3 text-sm font-medium hover:bg-white/20 transition-colors flex items-center justify-center gap-2">
                {loadingId === "pro_yearly" ? <Loader2 size={15} className="animate-spin" /> : null} {promoOn ? "Get Pro Free" : "Yearly"}
              </button>
            </div>
          </div>
        </div>

        {/* Ultra Pro — Coming Soon (Beta included with Pro) */}
        <div className="mt-10 relative overflow-hidden border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-8" data-testid="plan-ultra-pro">
          <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-indigo-600 text-white text-[10px] uppercase tracking-widest px-3 py-1 font-semibold">Coming Soon</div>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
              <Sparkles size={18} />
            </div>
            <div>
              <p className="overline text-indigo-700">MyAE Ultra Pro</p>
              <h2 className="font-serif text-3xl tracking-tight text-foreground mt-1">The next level of academic coaching</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                Ultra Pro is currently in Beta. <span className="font-semibold text-foreground">Pro subscribers get complimentary Beta access</span> while we refine and validate every feature with real students.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <div className="border border-indigo-100 bg-white/60 backdrop-blur-sm p-4">
              <p className="text-sm font-semibold text-zinc-900">Improve This</p>
              <p className="text-xs text-muted-foreground mt-1">Per-issue coaching that explains why a flag was raised and how to fix it in your own voice.</p>
            </div>
            <div className="border border-indigo-100 bg-white/60 backdrop-blur-sm p-4">
              <p className="text-sm font-semibold text-zinc-900">Evidence Finder</p>
              <p className="text-xs text-muted-foreground mt-1">Suggests strong sources for unsupported claims so your argument stands up to scrutiny.</p>
            </div>
            <div className="border border-indigo-100 bg-white/60 backdrop-blur-sm p-4">
              <p className="text-sm font-semibold text-zinc-900">Priority processing</p>
              <p className="text-xs text-muted-foreground mt-1">Faster reports and advanced lecturer-style feedback, plus everything new we build during Beta.</p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t border-indigo-100">
            <p className="text-xs text-muted-foreground max-w-xl">
              <span className="font-semibold text-foreground">Beta strategy:</span> Ultra Pro pricing will be published at public launch. Beta users who helped us shape the product will receive an early-adopter or grandfathered price.
            </p>
            {status?.plan === "pro" ? (
              <span className="inline-flex items-center gap-2 bg-indigo-600 text-white text-sm px-4 py-2 font-medium" data-testid="ultra-pro-beta-active">
                <Sparkles size={14} /> Beta access active
              </span>
            ) : (
              <button
                onClick={() => buy("pro_monthly")}
                disabled={loadingId}
                data-testid="ultra-pro-unlock-via-pro"
                className="inline-flex items-center justify-center gap-2 bg-oxford text-white text-sm px-4 py-2 hover:bg-[#2C5282] transition-colors"
              >
                {loadingId === "pro_monthly" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={14} />}
                Unlock Beta with Pro
              </button>
            )}
          </div>
        </div>

        {/* Credits */}
        <div className="mt-10 border border-border bg-card p-8" data-testid="credit-packs">
          <p className="overline text-muted-foreground">Pay-as-you-go credits</p>
          <p className="text-sm text-muted-foreground mt-1 mb-5">{promoOn ? "Future pay-as-you-go pricing — currently free while we grow." : "No subscription — top up around deadlines."}</p>
          <div className="grid sm:grid-cols-3 gap-px bg-border border border-border">
            {creditPacks.map((c) => (
              <div key={c.id} className="bg-card p-5 text-center" data-testid={`credit-${c.id}`}>
                <p className="font-serif text-2xl">{c.n}</p>
                {promoOn ? (
                  <div className="mt-1"><FreePrice className="font-mono text-2xl font-semibold text-oxford">{c.price}</FreePrice></div>
                ) : (
                  <p className="font-mono text-3xl font-semibold text-oxford mt-1">{c.price}</p>
                )}
                {c.tag && !promoOn && <p className="text-[11px] text-[#15803D] font-medium mt-0.5">{c.tag}</p>}
                <button onClick={() => buy(c.id)} disabled={loadingId} data-testid={`buy-${c.id}`}
                  className="mt-4 w-full bg-oxford text-white py-2.5 text-sm hover:bg-[#2C5282] transition-colors flex items-center justify-center gap-2">
                  {loadingId === c.id ? <Loader2 size={15} className="animate-spin" /> : null} {promoOn ? "Get Free" : "Buy"}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Redeem promo code — beta codes, uni promos, marketing campaigns */}
        <div className="mt-8 border border-border bg-card p-6 max-w-xl mx-auto" data-testid="redeem-card">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-oxford/10 flex items-center justify-center text-oxford shrink-0"><Ticket size={16} /></div>
            <div>
              <p className="text-sm font-semibold text-foreground">Have a promo code?</p>
              <p className="text-xs text-muted-foreground">Redeem beta-tester, university or campaign codes to unlock Pro time or credits instantly.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
              placeholder="AE-XXXXXXXX" data-testid="redeem-input"
              className="flex-1 border border-border px-3 py-2.5 text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-1 focus:ring-oxford" />
            <button onClick={redeem} disabled={redeeming || !redeemCode.trim()} data-testid="redeem-btn"
              className="bg-oxford text-white text-sm px-5 py-2.5 hover:bg-[#2C5282] transition-colors flex items-center gap-2 disabled:opacity-60">
              {redeeming ? <Loader2 size={15} className="animate-spin" /> : "Redeem"}
            </button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">Secure payment via Stripe. Cancel anytime. Prices in GBP.</p>
      </div>
    </div>
  );
}
